// Account creation / sign-in option list shared by the post-booking modal
// success screen and the portal /portal/sign-in page. Five entries:
//
//   1. Continue with Google     → OAuth redirect via Devise omniauth
//   2. Continue with Apple      → OAuth redirect via Devise omniauth
//   3. Continue with Facebook   → OAuth redirect via Devise omniauth
//   4. Email me a login link    → POST /auth/magic_link (passwordless)
//   5. Create with password     → /register pre-filled with anything we know
//
// All five render in the same calm "Editorial Warm" pill style so the list
// reads as one cohesive choice rather than a jumble of brand-coloured CTAs.
//
// Each option is rendered as a separate button so the parent can intercept
// e.g. the magic-link click for an inline "check your email" state without
// the OAuth links needing a router-aware wrapper.

import { useState, type CSSProperties, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { requestMagicLink } from '../../services/magicLink'
import { nala } from './palette'

// OmniAuth appends its own /auth/:provider segment to the Devise mount
// path (which is /api/v1/auth), giving the literal /api/v1/auth/auth/:provider.
// Mirrors the URL builder in SocialLoginButtons.tsx so both surfaces hit
// the exact same endpoints.
const API_BASE   = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1') as string
const OAUTH_BASE = `${API_BASE}/auth/auth`
function oauthUrl(provider: 'google_oauth2' | 'facebook' | 'apple'): string {
  return `${OAUTH_BASE}/${provider}`
}

interface Props {
  // Pre-filled email for the magic-link button and for /register's query
  // string. Optional — when omitted, the magic-link button still works but
  // requires the visitor to have already typed their email somewhere upstream.
  email?: string
  // Extra query params to forward to /register (first_name, last_name,
  // phone, etc.) so the password sign-up form starts already filled in.
  registerPrefill?: Record<string, string | undefined>
}

export default function NalaAuthOptions({ email, registerPrefill }: Props) {
  const [linkSent, setLinkSent] = useState(false)
  const magicLink = useMutation({
    mutationFn: () => requestMagicLink(email ?? ''),
    onSuccess: () => setLinkSent(true),
  })

  // Build a deep-link to the marketing /register page that pre-fills
  // anything we already collected during booking, so the customer just
  // confirms + adds a password.
  const registerHref = (() => {
    const params = new URLSearchParams()
    if (email) params.set('email', email)
    Object.entries(registerPrefill ?? {}).forEach(([k, v]) => {
      if (v) params.set(k, v)
    })
    const qs = params.toString()
    return qs ? `/register?${qs}` : '/register'
  })()

  // OAuth providers strip unknown query params during the authorize
  // round-trip (the `?return=` we'd want to forward isn't preserved
  // unless we go through OmniAuth's session-backed `origin` mechanism).
  // For now every OAuth sign-in lands on /portal/dashboard, which is
  // the right destination for both BookingModal and the portal sign-in
  // surface. If a future caller needs a different post-auth landing,
  // wire it through omniauth_callbacks_controller using
  // `request.env['omniauth.origin']`.
  const oauthHref = (provider: 'google_oauth2' | 'facebook' | 'apple') => oauthUrl(provider)

  if (linkSent) {
    return (
      <div
        style={{
          padding: '14px 16px',
          borderRadius: 12,
          background: nala.soft,
          border: `1px solid ${nala.line}`,
          fontSize: 13.5,
          lineHeight: 1.55,
          color: nala.ink,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Check your email</div>
        <div style={{ color: nala.mute }}>
          We sent a one-tap sign-in link to <strong style={{ color: nala.ink }}>{email}</strong>.
          The link expires in 15 minutes.
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <PillLink as="anchor" href={oauthHref('google_oauth2')} icon={<GoogleGlyph />} label="Continue with Google" />
      <PillLink as="anchor" href={oauthHref('apple')}        icon={<AppleGlyph />}  label="Continue with Apple"  />
      <PillLink as="anchor" href={oauthHref('facebook')}     icon={<FacebookGlyph />} label="Continue with Facebook" />
      <PillLink
        as="button"
        onClick={() => email && !magicLink.isPending && magicLink.mutate()}
        disabled={!email || magicLink.isPending}
        icon={<EnvelopeGlyph />}
        label={magicLink.isPending ? 'Sending link…' : 'Email me a login link'}
      />
      <PillLink as="anchor" href={registerHref} icon={<LockGlyph />} label="Create with password" />
      {magicLink.isError && (
        <div style={{ color: nala.clay, fontSize: 12.5, marginTop: 2 }}>
          We couldn&apos;t send the link just now. Please try again or use another option.
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Visual primitive — every auth option uses the exact same chrome so the
// list reads as a uniform pick-list rather than a stack of brand pills.
// ─────────────────────────────────────────────────────────────────────────

interface PillProps {
  icon: ReactNode
  label: string
}
type PillAnchorProps   = PillProps & { as: 'anchor';  href: string }
type PillButtonProps   = PillProps & { as: 'button';  onClick: () => void; disabled?: boolean }

function PillLink(props: PillAnchorProps | PillButtonProps) {
  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: `1px solid ${nala.line}`,
    background: '#fff',
    color: nala.ink,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background .15s, border-color .15s',
    boxSizing: 'border-box',
  }

  const iconWrap: CSSProperties = {
    width: 22,
    height: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }

  const content = (
    <>
      <span style={iconWrap}>{props.icon}</span>
      <span>{props.label}</span>
    </>
  )

  if (props.as === 'anchor') {
    return (
      <a
        href={props.href}
        style={baseStyle}
        onMouseEnter={(e) => (e.currentTarget.style.background = nala.surface)}
        onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
      >
        {content}
      </a>
    )
  }

  // For the magic-link button which needs disabled + dynamic state, we
  // can't use a plain Link — so a real <button> with matching chrome.
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      style={{
        ...baseStyle,
        opacity: props.disabled ? 0.55 : 1,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!props.disabled) e.currentTarget.style.background = nala.surface
      }}
      onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
    >
      {content}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Inline glyphs — all sized to fit the 22×22 wrap, in colours subtle
// enough to read as part of a calm editorial palette rather than the
// over-saturated brand marks the marketing site usually wants.
// ─────────────────────────────────────────────────────────────────────────

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
  )
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill={nala.ink}>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.39.07 2.35.74 3.15.8 1.19-.24 2.35-.93 3.62-.84 1.54.12 2.69.73 3.45 1.84-3.17 1.9-2.42 5.97.48 7.13-.57 1.53-1.32 3.04-2.7 3.95M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25" />
    </svg>
  )
}

function FacebookGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#1877F2">
      <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
    </svg>
  )
}

function EnvelopeGlyph() {
  return (
    <svg width="18" height="14" viewBox="0 0 20 16" fill="none" stroke={nala.mute} strokeWidth="1.6" aria-hidden="true">
      <rect x="1.5" y="2" width="17" height="12" rx="2.5" />
      <path d="M2 3.5l8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LockGlyph() {
  return (
    <svg width="16" height="18" viewBox="0 0 18 20" fill="none" stroke={nala.mute} strokeWidth="1.6" aria-hidden="true">
      <rect x="2" y="8.5" width="14" height="10" rx="2.5" />
      <path d="M5 8.5V6a4 4 0 1 1 8 0v2.5" strokeLinecap="round" />
    </svg>
  )
}

// Used by the portal sign-in page when it wants to keep the existing
// /register link as a fallback rather than relying on the password pill.
// Re-exported so callers don't need to import react-router-dom themselves.
export const RegisterLink = Link
